import _ from "lodash";
import { getProfileList, getUser, getTranslationType } from "@/api/account";
import {
  confirmEmailVerification,
  forgotPassword,
  getLanguages,
  getSystemConfig,
  getTimezoneList,
  login,
  logout,
  resetPassword,
  rotatePassword,
  sendEmailVerification,
  signup,
  updateProfile,
} from "@/api/user";
import {
  getApikey,
  getCurrentId,
  getToken,
  getUserInfo,
  removeAccount,
  removeApikey,
  removeCurrentId,
  removeSubList,
  removeToken,
  removeUserInfo,
  setApikey,
  setCurrentId,
  setToken,
  setUserInfo,
} from "@/utils/auth";
import { getReferralDetail } from "@/api/promotion";
import Vue from "vue";

// The cookie can hold the truthy string "null", or corrupt JSON — both used to yield a
// null user, which threw on every `this.user.x` read (NODE-EXPRESS-TV).
function parseUserInfo() {
  try {
    return JSON.parse(getUserInfo()) || {};
  } catch {
    return {};
  }
}

const state = {
  token: getToken(),
  apiKey: getApikey(),
  email: "",
  user: parseUserInfo(),
  profile: [],
  currentId: getCurrentId() ? JSON.parse(getCurrentId()) : {},
  timezoneList: [],
  systemConfig: {},
  language: {
    code: "en",
    is_active: true,
    is_default: true,
    locale_code: "en",
    name: "English",
    slug: "english",
    symbol: "EN",
  },
  languageList: [],
  languageUpdatedFlag: false,
  isSigned: true,
  translationTypes: [],
  referral_detail: {},
  // Time-boxed trial access — populated from the /profile subscription payload
  // (account/getProfile) and by the 426/42634 interceptor branch in utils/request.js.
  trial: {
    is_trial: false,
    is_trial_expired: false,
    prepaid_until: null,
  },

  payment_method_access_code: null,
  billing_history_access_code: null,
  pending_payment_access_code: null,
  usage_access_code: null,
  credit_history_access_code: null,
  roles_access_code: null,
  teams_access_code: null,
  teams_relation_access_code: null,
  subscription_access_code: null,
  user_access_code: null,
  custom_field_access_code: null,
  api_keys_access_code: null,
  api_keys_logs_access_code: null,
};
const mutations = {
  set_payment_method_access_code(state, payload) {
    state.payment_method_access_code = payload;
  },
  set_billing_history_access_code(state, payload) {
    state.billing_history_access_code = payload;
  },
  set_pending_payment_access_code(state, payload) {
    state.pending_payment_access_code = payload;
  },
  set_usage_access_code(state, payload) {
    state.usage_access_code = payload;
  },
  set_credit_history_access_code(state, payload) {
    state.credit_history_access_code = payload;
  },
  set_roles_access_code(state, payload) {
    state.roles_access_code = payload;
  },
  set_teams_access_code(state, payload) {
    state.teams_access_code = payload;
  },
  set_teams_relation_access_code(state, payload) {
    state.teams_relation_access_code = payload;
  },
  set_subscription_access_code(state, payload) {
    state.subscription_access_code = payload;
  },
  set_user_access_code(state, payload) {
    state.user_access_code = payload;
  },
  set_custom_field_access_code(state, payload) {
    state.custom_field_access_code = payload;
  },
  set_api_keys_access_code(state, payload) {
    state.api_keys_access_code = payload;
  },
  set_api_keys_logs_access_code(state, payload) {
    state.api_keys_logs_access_code = payload;
  },

  setLanguage(state, data) {
    if (!_.isEqual(state.language, data)) {
      state.language = data;
      localStorage.setItem("language", JSON.stringify(data));
    }
  },
  setActiveLanguage(state, data) {
    state.language = data;
  },
  setLanguageList(state, data) {
    state.languageList = data;
    localStorage.setItem("languageList", JSON.stringify(data));
  },
  setLanguageUpdatedFlag(state) {
    state.languageUpdatedFlag = !state.languageUpdatedFlag;
  },
  setToken(state, { token, email }) {
    state.token = token;
    state.email = email;
    if (state.isSigned) {
      setToken(token);
    }
  },
  setApikey(state, apiKey) {
    state.apiKey = apiKey;
    setApikey(apiKey);
  },
  setUserInfo(state, data) {
    // Never persist the string "null" — see parseUserInfo.
    setUserInfo(data ? JSON.stringify(data) : "");
    state.user = data || {};
  },
  setCurrentId(state, id) {
    setCurrentId(JSON.stringify(id));
    state.currentId = id;
  },
  removeUserInfo(unused_state) {
    removeToken();
    removeApikey();
    removeAccount();
    removeSubList();
    removeCurrentId();
    // Was missing: logout left the pubrio-user-info profile blob behind.
    removeUserInfo();
  },
  setProfile(state, profile) {
    profile ? (state.profile = profile) : (state.profile = []);
    localStorage.setItem("pubrio-profile", JSON.stringify(state.profile));
  },
  setTimezoneList(state, data) {
    data ? (state.timezoneList = data) : (state.timezoneList = []);
  },
  setIsSigned(state, data) {
    state.isSigned = data;
  },
  setSystemConfig(state, data) {
    data ? (state.systemConfig = data) : (state.systemConfig = {});
    // localStorage.setItem('systemConfig',JSON.stringify(state.systemConfig))
  },
  setTranslationTypes(state, data) {
    state.translationTypes = data;
  },
  setReferralDetail(state, data) {
    state.referral_detail = data;
  },
  // Accepts partial payloads (the interceptor only knows is_trial_expired + prepaid_until).
  setTrialState(state, data) {
    state.trial = { ...state.trial, ...data };
  },
};
const actions = {
  async login(context, data) {
    try {
      const result = await login(data);
      if (result && result.data) {
        let token = result?.data.login.access_token;
        let apiKey = result?.data.login.user.api_key;
        let email = result?.data.login.user.email;
        context.commit("setToken", { token, email });
        context.commit("setApikey", apiKey);
        const user = await getUser();
        context.commit("setUserInfo", user?.data);
        return user;
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async getLanguages({ commit, state }) {
    try {
      const data = await getLanguages();
      if (data.data) {
        commit("setLanguageList", data.data);
        const activeLanguage = data.data.find(
          (language) => language.code === state.language?.code
        );
        if (activeLanguage) {
          commit("setActiveLanguage", activeLanguage);
        }
      }
    } catch (err) {
      console.log(err);
    }
  },
  async getUserInfo(context, setCurrentId = true) {
    try {
      const profile = await getProfileList();
      context.commit("setProfile", profile?.data);
      setCurrentId && context.commit("setCurrentId", profile?.data[0]);
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async getTimezoneList(context) {
    try {
      const result = await getTimezoneList();
      context.commit("setTimezoneList", result?.data);
      return result?.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async signup(context, data) {
    try {
      const result = await signup(data);
      const payload = result?.data.signup;
      // New accounts must verify their email first — no token is issued yet.
      if (payload?.is_email_verification_required) {
        return {
          is_email_verification_required: true,
          email: payload.user?.email || data.email,
          resend_available_in_second:
            payload.verification?.resend_available_in_second,
        };
      }
      let token = payload.access_token;
      let apiKey = payload.user.api_key;
      let email = payload.user.email;
      context.commit("setToken", { token, email });
      context.commit("setApikey", apiKey);
      return { email };
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async sendEmailVerification(context, data) {
    const result = await sendEmailVerification(data);
    return result?.data?.email_verification;
  },
  async confirmEmailVerification(context, data) {
    // Verified. If the API returns the parked token (session confirmed live), auto-log-in — same
    // handling as the login action; otherwise the caller falls back to a fresh login.
    const result = await confirmEmailVerification(data);
    const payload = result?.data?.email_verification;
    if (payload?.access_token) {
      context.commit("setToken", {
        token: payload.access_token,
        email: payload.user?.email,
      });
      context.commit("setApikey", payload.user?.api_key);
    }
    return payload;
  },
  // Auto-log-in off the returned session, same as confirmEmailVerification; else the caller logs in fresh.
  async rotatePassword(context, data) {
    const result = await rotatePassword(data);
    const payload = result?.data?.password_rotation;
    if (payload?.access_token) {
      context.commit("setToken", {
        token: payload.access_token,
        email: payload.user?.email,
      });
      context.commit("setApikey", payload.user?.api_key);
    }
    return payload;
  },
  async forgotPassword(context, data) {
    try {
      const result = await forgotPassword({ email: data });
      Vue.$toast.success(result?.data.forgot_password.message);
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async resetPassword(context, data) {
    try {
      const result = await resetPassword(data);
      Vue.$toast.success(result?.data.reset_password.message);
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async logout({ commit, state, rootState }) {
    try {
      const unused_result = await logout({ email: state.email });
      commit("removeUserInfo");
      rootState.common.showNotificationBar &&
        commit("common/setNotificationBar", false, { root: true });
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async updateProfile(context, data) {
    try {
      await updateProfile(data);
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  /*----获取系统config----*/
  async getConfig(context, data) {
    try {
      const res = await getSystemConfig(data);
      context.commit("setSystemConfig", res?.data);

      const { default_notification_bar_delay_second } = res?.data || {};

      Vue.$toast.updateDefaults({
        timeout: (default_notification_bar_delay_second ?? 2) * 1000,
      });
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async getTranslationTypes({ commit }, data) {
    try {
      const res = await getTranslationType(data);
      commit("setTranslationTypes", res?.data);
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async getUserConfig(context) {
    try {
      const user = await getUser();
      context.commit("setUserInfo", user?.data);
      return user;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async getReferrals({ commit }, data) {
    try {
      const res = await getReferralDetail({
        ...data,
      });
      commit("setReferralDetail", res?.data);
      return res;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
